import React, { useState, useEffect, useMemo } from "react";
import { format, addDays, startOfWeek } from "date-fns";
import { isBefore, startOfDay } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderOpen,faClock  } from '@fortawesome/free-solid-svg-icons';

const getColorByStatus = (status) => {
  switch (status?.toLowerCase()) {
    case "done":
      return "text-gray-700 border-green-500 bg-green-200";
    case "to do":
      return "text-gray-700 border-gray-400 bg-white";
    case "doing":
      return "text-gray-700 border-yellow-400 bg-yellow-200";
    default:
      return "text-gray-700 border-gray-400 bg-white";
  }
};



// ✅ Time formatter (e.g. 7.93 => 7:56)
const formatDecimalHoursToTime = (hoursFloat) => {
  const totalMinutes = Math.round(hoursFloat * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}`;
};

const UserStatsTableView = ({selectedUser}) => {
    const [weekStartDate, setWeekStartDate] = useState(() => {
      const today = new Date();
      return today.toISOString().split('T')[0]; // "YYYY-MM-DD"
    });  
    const [tasks, setTasks] = useState([]);
    const [highlightedTaskName, setHighlightedTaskName] = useState(null);
       



  const weekStart = startOfWeek(new Date(weekStartDate), { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekDates = days.map((d) => format(d, "yyyy-MM-dd"));



  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const res = await fetch(`/api/getTasksByUser.php?user=${selectedUser}`);
        const data = await res.json();
        setTasks(data);
      } catch (err) {
        console.error("❌ Error fetching tasks:", err);
      }
    };
    if (selectedUser) fetchTasks();
  }, [selectedUser]);

  const [projectNames, setProjectNames] = useState({});

useEffect(() => {
  const fetchProjects = async () => {
    try {
      const res = await fetch("/api/projectname.php?method=getNames");
      const data = await res.json();
      if (data.success) {
        setProjectNames(data.projects); // { project_id: "Project Name" }
      }
    } catch (error) {
      console.error("Failed to load project names:", error);
    }
  };

  fetchProjects();
}, []);


  

const clampSessionToWorkingHours = (start, end) => {
  const WORK_INTERVALS = [
    { from: { h: 9, m: 30 }, to: { h: 13, m: 30 } },
    { from: { h: 14, m: 0 }, to: { h: 18, m: 0 } },
  ];

  const startDate = new Date(start);
  const endDate = new Date(end);

  // If end is not same day, clamp to 6:00 PM on start day
  if (startDate.toDateString() !== endDate.toDateString()) {
    endDate.setTime(startDate.getTime()); // reset to start day
    endDate.setHours(18, 0, 0, 0); // 6:00 PM of the start day
  }

  const day = startDate.getDay(); // Sunday = 0
  if (day === 0) return 0; // No work on Sunday

  let totalMs = 0;

  for (const { from, to } of WORK_INTERVALS) {
    const intervalStart = new Date(startDate);
    intervalStart.setHours(from.h, from.m, 0, 0);

    const intervalEnd = new Date(startDate);
    intervalEnd.setHours(to.h, to.m, 0, 0);

    const actualStart = new Date(Math.max(intervalStart, startDate));
    const actualEnd = new Date(Math.min(intervalEnd, endDate));

    if (actualStart < actualEnd) {
      totalMs += actualEnd - actualStart;
    }
  }

  return totalMs;
};

  

  const dayTaskMap = useMemo(() => {
      const map = {};
  
     
      tasks.forEach((task) => {
        let sessions = [];
        try {
          sessions = JSON.parse(task.duration || "[]");
        } catch {
          sessions = [];
        }
  
        const taskStartDate = task.start_date;
  
        sessions.forEach((session) => {
          if (!session.start) return;
          const start = new Date(session.start);
          const end = session.end
            ? new Date(session.end)
            : session.pause
            ? new Date(session.pause)
            : new Date();
  
          const sessionDate = start.toISOString().split("T")[0];
          const durationMs = clampSessionToWorkingHours(start, end);
          const hours = parseFloat((durationMs / 3600000).toFixed(2));
          if (!map[sessionDate]) map[sessionDate] = {};
          const key = task.task_name;
          if (!map[sessionDate][key]) {
            map[sessionDate][key] = {
              id: task.id,
              taskName: task.task_name,
              hours: 0,
              status:task.status,
              assign_by: task.assign_by,
              projectName: projectNames[task.project_id] || "",
            };
          }
          map[sessionDate][key].hours += hours;
        });
  
        if (taskStartDate && (!sessions || sessions.length === 0)) {
          if (!map[taskStartDate]) map[taskStartDate] = {};
          const key = task.task_name;
          const scheduledHours = parseFloat(task.submission_date || 0);
          if (!map[taskStartDate][key]) {
            map[taskStartDate][key] = {
              id: task.id,
              taskName: task.task_name,
              hours: scheduledHours,
              scheduledOnly: true,
              status:task.status,
              assign_by:task.assign_by,
              projectName: projectNames[task.project_id] || "",
            };
          } else {
            map[taskStartDate][key].hours += scheduledHours;
          }
        }
      });
  
      const result = {};
      for (const date in map) {
        result[date] = Object.values(map[date]).map((t) => ({
          ...t,
          duration: formatDecimalHoursToTime(t.hours),
          hours: parseFloat(t.hours.toFixed(2)),
        }));
      }
  
      return result;
    }, [tasks, projectNames]);

  return (
    <div className="space-y-4 mt-1 p-7 max-h-screen ">
      <div className="flex items-center justify-between">
        <div className="text-xl font-semibold text-gray-700">Time Sheet</div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Select Week:</label>
          <input
            type="date"
            value={format(weekStart, "yyyy-MM-dd")}
            onChange={(e) => setWeekStartDate(e.target.value)}
            className="px-2 py-1 rounded-md text-xs shadow-sm focus:ring focus:ring-blue-200"
          />
        </div>
        
      </div>

      <div className="overflow-x-auto rounded-lg shadow-xl bg-white">
        <div className="grid grid-cols-7 text-center border border-gray-200 bg-gray-700 text-sm font-medium">
          {days.map((day, idx) => (
            <div key={idx} className="px-4 py-2 border-r border-gray-200 text-white">
              <div className="text-xs uppercase">{format(day, "EEE")}</div>
              <div className="text-[11px] font-semibold">{format(day, "MMM d")}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 text-sm min-h-[400px]">
          {weekDates.map((date, idx) => {
            const dayTasks = dayTaskMap[date] || [];
            const totalHours = dayTasks.reduce((sum, t) => sum + t.hours, 0);
            const remaining = parseFloat((8 - totalHours).toFixed(2));
            const isFuture = new Date(date) > new Date();

            return (
              <div
                key={idx}
                className={`border-r border-gray-300 p-4 transition-all min-h-[100px] 
                ${isFuture ? "bg-gray-200" : "bg-gray-50 hover:bg-gray-200"}`}
              >
                {dayTasks.map((t, i) => (
              <div
  key={i}
  onClick={() => setHighlightedTaskName(prev => prev === t.taskName ? null : t.taskName)}
  className={`
    text-xs rounded-md px-2 py-2 mb-2 shadow-md border-[0] cursor-pointer 
    ${getColorByStatus(t.status)} 
    ${highlightedTaskName === t.taskName ? "ring-2 ring-blue-500" : ""}
  `}
>
  <div className="text-[11px]  italic truncate">
    <FontAwesomeIcon icon={faFolderOpen} className="text-yellow-500 mr-1"/>{t.projectName ? t.projectName : "Single Task"}
  </div>
    <div className="font-semibold text-center">{t.taskName}</div>

    <div className="flex justify-between">
      <div className="text-[11px] italic">
    <FontAwesomeIcon icon={faClock} className="mr-1"/>{t.scheduledOnly ? "Scheduled" : `${t.duration}h`}
  </div>

  <div className="text-[10px]">
  @{t.assign_by?.split(' ')[0]}
</div>


    </div>

  
</div>


                ))}

                {remaining > 0 && !isBefore(startOfDay(new Date(date)), startOfDay(new Date())) && (
                  <>
                    <div
                      className="group relative w-full text-xs rounded-md px-2 py-1 mb-1 bg-gray-200 border border-dotted border-gray-400 text-center h-6 flex items-center justify-center cursor-pointer"
                    >
                      <span className=" text-gray-700">
                        Free time: {formatDecimalHoursToTime(remaining)}h
                      </span>
                     
                    </div>

                   
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default UserStatsTableView;
